/**
 * useChatState Hook
 * Orchestrator hook that combines all chat-related hooks
 * This is the main entry point for components to access chat functionality
 */

import { createContext, useCallback, useContext, useMemo } from 'react';
import { MESSAGE_ROLES } from '../constants';
import useChatService from './useChatService';
import useMessages from './useMessages';
import useStreaming from './useStreaming';

// ============================================================================
// Chat Context
// ============================================================================

export const ChatContext = createContext(null);

export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        console.warn('useChatContext must be used within ChatProvider');
    }
    return context;
};

/**
 * Main hook for chat functionality
 * Combines connection, streaming, and messages into a single interface
 * 
 * @param {Object} options - Hook options
 * @param {boolean} options.autoConnect - Auto-connect on mount
 * @returns {Object} Combined chat state and actions
 */
const useChatState = ({ autoConnect = false } = {}) => {
    // Chat service (WebSocket + HTTP)
    const chatService = useChatService({ autoConnect });

    // Streaming content
    const streaming = useStreaming(chatService);

    // Messages (conversation history + UI messages)
    const messages = useMessages(chatService);

    /**
     * Send a message to the backend
     * @param {string} content - Message content
     * @param {string} mode - 'discuss' or 'act'
     * @returns {Promise<Object>}
     */
    const sendMessage = useCallback(async (content, mode = 'discuss') => {
        if (!content.trim()) return;

        // Add user message to list (this also shows step indicator)
        const userMessage = messages.addUserMessage(content);
        if (!userMessage) return;

        // Set streaming to pending
        streaming.setPending();

        try {
            // Ensure connected before sending
            if (!chatService.isConnected) {
                await chatService.connect();
            }

            // Get all messages for context
            // Note: We need to include the new user message since state updates are async
            const existingMessages = messages.getMessagesForBackend();
            const allMessages = [
                ...existingMessages,
                { role: 'user', content: userMessage.content }
            ];

            // Send via chat service (WebSocket or HTTP)
            const result = await chatService.sendMessage(allMessages, null, mode);

            return result;
        } catch (error) {
            // Don't throw - just log the error, keep the UI showing
            console.error('Failed to send message:', error.message);
            // Keep step indicator showing for demo purposes
            // In production, you'd want to show an error state
        }
    }, [chatService, messages, streaming]);

    /**
     * Retry the last failed message
     */
    const retryLastMessage = useCallback(async () => {
        const history = messages.conversationHistory;
        const lastUserMessage = [...history]
            .reverse()
            .find(m => m.role === MESSAGE_ROLES.USER);

        if (lastUserMessage) {
            // Clear error state
            messages.setError(null);
            streaming.setPending();

            try {
                const allMessages = messages.getMessagesForBackend();
                await chatService.sendMessage(allMessages, null, 'discuss');
            } catch (error) {
                messages.setError(error.message || 'Failed to retry');
                streaming.reset();
            }
        }
    }, [chatService, messages, streaming]);

    /**
     * Clear conversation and reset state
     */
    const clearConversation = useCallback(() => {
        messages.clearMessages();
        streaming.reset();
    }, [messages, streaming]);

    /**
     * Copy last assistant response to clipboard
     * @returns {Promise<boolean>} Success status
     */
    const copyLastResponse = useCallback(async () => {
        const lastAssistantMessage = [...messages.conversationHistory]
            .reverse()
            .find(m => m.role === MESSAGE_ROLES.ASSISTANT);

        if (lastAssistantMessage?.content) {
            try {
                await navigator.clipboard.writeText(lastAssistantMessage.content);
                return true;
            } catch (error) {
                console.error('Failed to copy:', error);
                return false;
            }
        }
        return false;
    }, [messages.conversationHistory]);

    /**
     * Stop the current generation
     */
    const stopGeneration = useCallback(() => {
        // Reset streaming state
        streaming.reset();
        // Hide step indicator
        messages.removeStepIndicator?.();
        console.log('Generation stopped by user');
    }, [streaming, messages]);

    // Combined state for convenience
    const state = useMemo(() => ({
        // Connection
        connectionState: chatService.connectionState,
        isConnected: chatService.isConnected,
        isConnecting: chatService.isConnecting,
        isUsingHTTP: chatService.isUsingHTTP,
        connectionError: chatService.error,

        // Messages
        uiMessages: messages.uiMessages,
        conversationHistory: messages.conversationHistory,
        hasMessages: messages.hasMessages,
        isLoading: messages.isLoading,
        messageError: messages.error,
        showStepIndicator: messages.showStepIndicator,

        // Streaming - true when AI is thinking/responding
        streamingState: streaming.streamingState,
        streamingContent: streaming.content,
        isStreaming: streaming.isStreaming || streaming.isPending || messages.showStepIndicator,
        isPending: streaming.isPending
    }), [chatService, messages, streaming]);

    // Combined actions
    const actions = useMemo(() => ({
        // Connection
        connect: chatService.connect,
        disconnect: chatService.disconnect,

        // Messages
        sendMessage,
        retryLastMessage,
        clearConversation,
        copyLastResponse,
        stopGeneration,
        addUserMessage: messages.addUserMessage
    }), [
        chatService,
        sendMessage,
        retryLastMessage,
        clearConversation,
        copyLastResponse,
        stopGeneration,
        messages.addUserMessage
    ]);

    return {
        ...state,
        ...actions
    };
};

export default useChatState;
